import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Button, Card } from '../../../components/common';
import SEO from '../../../components/common/SEO';
import PartnerApplyModal from '../../../components/service/PartnerApplyModal';

export default function Partnership() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPartnerModal, setShowPartnerModal] = useState(false);

  const whyChooseUs = [
    {
      icon: 'business',
      title: 'Global Corporate Experience',
      desc: t('info.partnership_why1_desc'),
    },
    {
      icon: 'person',
      title: 'Certified Professional Guides',
      desc: t('info.partnership_why2_desc'),
    },
    {
      icon: 'star',
      title: 'Tailored Business Protocol',
      desc: t('info.partnership_why3_desc'),
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO route="/partnership" />
      {/* Hero - Dark bg */}
      <div className="bg-blue-900 py-16 text-center px-5">
        <h1 className="text-2xl font-extrabold text-white mb-4">{t('info.partnership_hero_title')}</h1>
        <p className="text-sm sm:text-base text-white/80 leading-relaxed break-keep">
          {t('info.partnership_hero_desc1')}<br />
          {t('info.partnership_hero_desc2')}
        </p>
        <p className="text-xs sm:text-sm text-white/60 mt-3 italic break-keep">
          Your Premier Partner for Corporate Protocol &amp; Mobility
        </p>
      </div>

      {/* Why Choose Us */}
      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-8 sm:py-12">
        <h2 className="text-lg font-bold text-mono-900 text-center mb-8">{t('info.partnership_why')}</h2>
        <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-form mx-auto mb-12">
          {whyChooseUs.map((item) => (
            <div key={item.title} className="text-center flex flex-col items-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-3 sm:mb-4">
                <Icon name={item.icon} size={24} className="text-orange-500 sm:hidden" />
                <Icon name={item.icon} size={28} className="text-orange-500 hidden sm:block" />
              </div>
              <h3 className="text-2sm sm:text-sm font-bold text-mono-900 mb-1 sm:mb-2 break-keep flex-1 flex items-center">{item.title}</h3>
              <p className="text-xs sm:text-2sm text-mono-500">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-form mx-auto items-stretch">
          {/* Corporate Inquiry */}
          <Card variant="auth" className="flex flex-col h-full">
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-5">
              <Icon name="business" size={26} className="text-orange-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-mono-900 mb-2 tracking-tight">{t('info.partnership_corp_title')}</h2>
            <p className="text-sm text-mono-500 leading-relaxed mb-6 whitespace-pre-line break-keep">
              {t('info.partnership_corp_desc')}
            </p>
            <div className="border-t border-mono-100 pt-5 mb-6 text-sm leading-relaxed space-y-4">
              <p className="text-base font-bold text-mono-900 tracking-tight">{t('info.partnership_corp_benefits')}</p>
              <div>
                <p className="font-bold text-mono-800">{t('info.partnership_corp_benefit1_title')}</p>
                <p className="text-mono-500">{t('info.partnership_corp_benefit1_desc')}</p>
              </div>
              <div>
                <p className="font-bold text-mono-800">{t('info.partnership_corp_benefit2_title')}</p>
                <p className="text-mono-500">{t('info.partnership_corp_benefit2_desc')}</p>
              </div>
              <div>
                <p className="font-bold text-mono-800">{t('info.partnership_corp_benefit3_title')}</p>
                <p className="text-mono-500">{t('info.partnership_corp_benefit3_desc')}</p>
              </div>
            </div>
            <div className="mt-auto">
              <Button level="level1" fullWidth onClick={() => navigate('/faq')}>
                {t('info.partnership_corp_button')}
              </Button>
            </div>
          </Card>

          {/* Partner Signup */}
          <Card variant="auth" className="flex flex-col h-full">
            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-5">
              <Icon name="person" size={26} className="text-orange-500" />
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-mono-900 mb-2 tracking-tight">{t('info.partnership_partner_title')}</h2>
            <p className="text-sm text-mono-500 leading-relaxed mb-6 whitespace-pre-line break-keep">
              {t('info.partnership_partner_desc')}
            </p>
            <div className="border-t border-mono-100 pt-5 mb-6 text-sm leading-relaxed space-y-4">
              <p className="text-base font-bold text-mono-900 tracking-tight">{t('info.partnership_benefits')}</p>
              <div>
                <p className="font-bold text-mono-800">{t('info.partnership_benefit1_title')}</p>
                <p className="text-mono-500">{t('info.partnership_benefit1_desc')}</p>
              </div>
              <div>
                <p className="font-bold text-mono-800">{t('info.partnership_benefit2_title')}</p>
                <p className="text-mono-500">{t('info.partnership_benefit2_desc')}</p>
              </div>
              <div>
                <p className="font-bold text-mono-800">{t('info.partnership_benefit3_title')}</p>
                <p className="text-mono-500">{t('info.partnership_benefit3_desc')}</p>
              </div>
            </div>
            <div className="mt-auto">
              <Button level="dark" fullWidth onClick={() => setShowPartnerModal(true)}>{t('info.partnership_partner_button')}</Button>
            </div>
          </Card>
        </div>
      </div>

      {showPartnerModal && <PartnerApplyModal onClose={() => setShowPartnerModal(false)} />}
    </div>
  );
}
